import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { 
  Shield, Users, Building2, TrendingUp, Award, Briefcase, 
  Target, Brain, Bell, FileText, BarChart3, Lock, Sparkles,
  CheckCircle2, Zap, Globe, UserCheck
} from "lucide-react";
import FoundingPartnerCTA from "@/components/landing/FoundingPartnerCTA";
import TeamCredibility from "@/components/landing/TeamCredibility";
import TechnologyAdvantage from "@/components/landing/TechnologyAdvantage";
import RiskReversal from "@/components/landing/RiskReversal";
import StatsSection from "@/components/landing/StatsSection";
import ProblemStatement from "@/components/landing/ProblemStatement";
import HeroImage from "@/components/landing/HeroImage";
import StakeholderValueProps from "@/components/landing/StakeholderValueProps";
import { Testimonials } from "@/components/landing/Testimonials";
import { SmallSchoolValueProp } from "@/components/landing/SmallSchoolValueProp";

const Index = () => {
  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    document.querySelectorAll('.fade-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md shadow-float-1">
        <div className="max-w-[1400px] mx-auto px-8 h-14 flex items-center justify-between">
          <div className="text-sm font-light tracking-wider text-foreground">
            AthleteNexus
          </div>
          <div className="flex items-center gap-8">
            <button
              onClick={() => scrollToSection("platforms")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Platforms
            </button>
            <button
              onClick={() => scrollToSection("features")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Features
            </button>
            <button
              onClick={() => navigate("/pricing")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate("/resources")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Resources
            </button>
            <button
              onClick={() => navigate("/demo-workflow")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Demo
            </button>
            <button
              onClick={() => navigate("/compliance-checklist")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Free Checklist
            </button>
            <button
              onClick={() => navigate("/auth")}
              className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-48 pb-16 px-8 bg-gradient-to-b from-background to-muted/20">
        <div className="max-w-[800px] mx-auto text-center">
          <h1 className="text-[64px] md:text-[48px] sm:text-[36px] font-light leading-[1.15] tracking-tight text-foreground mb-6 fade-on-scroll">
            Compliance Infrastructure for Athletic Departments
          </h1>
          <p className="text-xl md:text-lg sm:text-base font-light text-muted-foreground leading-[1.6] mb-12 max-w-[700px] mx-auto fade-on-scroll" style={{ animationDelay: '0.15s' }}>
            Not a marketplace — institutional defense against NCAA violations, Title IX lawsuits, and $22M+ revenue sharing complexity
          </p>
          
          <HeroImage />
          
          <div className="fade-on-scroll" style={{ animationDelay: '0.6s' }}>
            <Button
              variant="minimal"
              size="lg"
              onClick={() => window.open("https://cal.com/jay-gauthier-the-mad-jotter-lhon2h/30min?overlayCalendar=true", "_blank")}
              className="h-14 px-12 text-base hover:scale-[1.02] transition-transform"
            >
              Schedule a Demo
            </Button>
            <p className="text-xs text-muted-foreground/60 mt-4 font-light">
              Custom institutional pricing available
            </p>
            <p className="text-sm text-muted-foreground mt-2 font-light">
              Schedule a personalized demo → 30 minutes
            </p>
          </div>
        </div>
      </section>

      {/* Founding Partner CTA */}
      <FoundingPartnerCTA />

      {/* Problem Statement */}
      <ProblemStatement />
      
      {/* Stakeholder Value Props */}
      <StakeholderValueProps />
      
      {/* Small School Value Prop */}
      <SmallSchoolValueProp />
      
      {/* Testimonials */}
      <Testimonials />
      
      {/* Team Credibility */}
      <TeamCredibility />
      
      {/* Technology Advantage */}
      <TechnologyAdvantage />

      {/* Stats Section */}
      <StatsSection />

      {/* Portal Types Section */}
      <section id="platforms" className="pb-32 px-8">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-4">
              Complete Ecosystem
            </h2>
            <h3 className="text-4xl font-light text-foreground mb-4">
              Six Specialized Portals for Every Stakeholder
            </h3>
            <p className="text-lg font-light text-muted-foreground max-w-[700px] mx-auto">
              From institutional compliance to athlete empowerment, each portal is purpose-built for specific NIL ecosystem roles
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-10 group bg-card hover:scale-[1.01] transition-all duration-base">
              <Shield className="w-10 h-10 mb-6 text-primary/80 group-hover:scale-110 transition-transform stroke-[1.5]" />
              <h4 className="text-xl font-normal mb-3">Institution Portal</h4>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Athletic departments and compliance officers manage deals, monitor conflicts, and ensure NCAA compliance with automated workflows
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Deal approval workflows
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Conflict detection
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Revenue sharing (CAPS)
                </li>
              </ul>
            </Card>

            <Card className="p-10 group bg-card hover:scale-[1.01] transition-all duration-base">
              <Users className="w-10 h-10 mb-6 text-primary/80 group-hover:scale-110 transition-transform stroke-[1.5]" />
              <h4 className="text-xl font-normal mb-3">Athlete Portal</h4>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Student-athletes submit deals, track earnings, manage deliverables, and access educational resources for NIL success
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Deal submission & tracking
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Earnings dashboard
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Educational resources
                </li>
              </ul>
            </Card>

            <Card className="p-10 group bg-card hover:scale-[1.01] transition-all duration-base">
              <Building2 className="w-10 h-10 mb-6 text-primary/80 group-hover:scale-110 transition-transform stroke-[1.5]" />
              <h4 className="text-xl font-normal mb-3">Brand Portal</h4>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Companies discover athletes, launch campaigns, track ROI, and manage partnerships through a compliant marketplace
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Athlete marketplace
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Campaign management
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Performance analytics
                </li>
              </ul>
            </Card>

            <Card className="p-10 group bg-card hover:scale-[1.01] transition-all duration-base">
              <Briefcase className="w-10 h-10 mb-6 text-primary/80 group-hover:scale-110 transition-transform stroke-[1.5]" />
              <h4 className="text-xl font-normal mb-3">Consulting Firm Portal</h4>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Advisory firms manage multiple institutions with cross-institution dashboards, white-label options, and analytics
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Multi-institution access
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  White-label branding
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Aggregated analytics
                </li>
              </ul>
            </Card>

            <Card className="p-10 group bg-card hover:scale-[1.01] transition-all duration-base">
              <Target className="w-10 h-10 mb-6 text-primary/80 group-hover:scale-110 transition-transform stroke-[1.5]" />
              <h4 className="text-xl font-normal mb-3">Collective Portal</h4>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                NIL collectives distribute funds to athletes, manage member rosters, track distributions, and ensure Title IX compliance
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Fund distribution
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Member management
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Title IX monitoring
                </li>
              </ul>
            </Card>

            <Card className="p-10 group bg-card hover:scale-[1.01] transition-all duration-base">
              <Award className="w-10 h-10 mb-6 text-primary/80 group-hover:scale-110 transition-transform stroke-[1.5]" />
              <h4 className="text-xl font-normal mb-3">Agency Portal</h4>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Athlete representation agencies manage rosters, negotiate deals, track commissions, and coordinate brand partnerships
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Athlete roster management
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Deal negotiation tools
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary/70" />
                  Commission tracking
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="pb-40 px-8 bg-background">
        <div className="max-w-[1100px] mx-auto py-24">
          <div className="text-center mb-20">
            <h2 className="text-sm font-light uppercase tracking-wider text-muted-foreground mb-4">
              Platform Capabilities
            </h2>
            <h3 className="text-4xl font-light text-foreground mb-4">
              Enterprise-Grade Features Built for NIL
            </h3>
            <p className="text-lg font-light text-muted-foreground max-w-[700px] mx-auto">
              Comprehensive toolset covering compliance, analytics, automation, and stakeholder management
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="text-center fade-on-scroll">
              <Brain className="w-8 h-8 mx-auto mb-4 text-primary/80 stroke-[1.5]" />
              <h4 className="text-lg font-normal mb-2">AI-Powered Analysis</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Automated deal risk assessment, conflict detection, and compliance recommendations
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <Zap className="w-8 h-8 mx-auto mb-4 text-primary/80 stroke-[1.5]" />
              <h4 className="text-lg font-normal mb-2">Automated Workflows</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Streamlined approval processes, automated notifications, and deadline tracking
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <Lock className="w-8 h-8 mx-auto mb-4 text-primary/80 stroke-[1.5]" />
              <h4 className="text-lg font-normal mb-2">Conflict Detection</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Real-time scanning against institutional sponsors and prohibited categories
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <FileText className="w-8 h-8 mx-auto mb-4 text-primary/80 stroke-[1.5]" />
              <h4 className="text-lg font-normal mb-2">Document Management</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Secure storage, version control, and e-signature integration for contracts
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <BarChart3 className="w-8 h-8 mx-auto mb-4 text-primary/80 stroke-[1.5]" />
              <h4 className="text-lg font-normal mb-2">Advanced Analytics</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Real-time dashboards, custom reports, and predictive insights
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <Bell className="w-8 h-8 mx-auto mb-4 text-primary/80 stroke-[1.5]" />
              <h4 className="text-lg font-normal mb-2">Smart Notifications</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Configurable alerts for deadlines, high-value deals, and compliance issues
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <TrendingUp className="w-8 h-8 mx-auto mb-4 text-primary/80 stroke-[1.5]" />
              <h4 className="text-lg font-normal mb-2">Title IX Monitoring</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Gender equity tracking, distribution analysis, and compliance reporting
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <Globe className="w-8 h-8 mx-auto mb-4 text-primary/80 stroke-[1.5]" />
              <h4 className="text-lg font-normal mb-2">Campaign Management</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Multi-athlete campaigns, budget tracking, and ROI measurement
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <Sparkles className="w-10 h-10 mx-auto mb-4 text-primary" />
              <h4 className="text-lg font-semibold mb-2">Athlete Marketplace</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Discover talent, filter by demographics, and connect brands with athletes
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <UserCheck className="w-10 h-10 mx-auto mb-4 text-primary" />
              <h4 className="text-lg font-semibold mb-2">Revenue Sharing (CAPS)</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Automated distribution pools, payment tracking, and compliance validation
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <Shield className="w-10 h-10 mx-auto mb-4 text-primary" />
              <h4 className="text-lg font-semibold mb-2">SOC 2 Compliant Audit Trails</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Cryptographically-verified immutable records with hash-chain integrity for regulatory defense
              </p>
            </div>

            <div className="text-center fade-on-scroll">
              <FileText className="w-10 h-10 mx-auto mb-4 text-primary" />
              <h4 className="text-lg font-semibold mb-2">Deliverable Tracking</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Monitor athlete obligations, proof of completion, and performance metrics
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Divider */}
      <div className="max-w-[1200px] mx-auto px-8 my-16">
        <div className="h-px bg-border/30" />
      </div>

      {/* Risk Reversal */}
      <RiskReversal />

      {/* Core Capabilities - Simplified */}
      <section className="pb-32 px-8">
        <div className="max-w-[600px] mx-auto">
          <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-12 text-center">
            Core Capabilities
          </h2>
          <div className="space-y-8">
            <div className="text-center">
              <h3 className="text-xl font-light text-foreground mb-2">
                Automated Compliance Validation
              </h3>
              <p className="text-base font-light text-muted-foreground leading-relaxed">
                Real-time conflict detection against institutional sponsors and prohibited categories
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-light text-foreground mb-2">
                Immutable Audit Trails
              </h3>
              <p className="text-base font-light text-muted-foreground leading-relaxed">
                SOC 2 Type II compliant records with cryptographic hash-chain verification for regulatory defense and institutional protection
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-light text-foreground mb-2">
                Revenue Sharing Management
              </h3>
              <p className="text-base font-light text-muted-foreground leading-relaxed">
                CAPS-compliant distribution tracking with Title IX equity monitoring
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-32 px-8">
        <div className="max-w-[800px] mx-auto text-center">
          <div className="h-px bg-border/30 mb-16" />
          <h2 className="text-4xl font-light text-foreground mb-6">
            Built for institutions that can't afford risk
          </h2>
          <p className="text-lg font-light text-muted-foreground mb-12 max-w-[500px] mx-auto">
            Join athletic departments protecting their programs with institutional-grade compliance infrastructure
          </p>
          <Button
            variant="minimal"
            size="lg"
            onClick={() => window.open("https://cal.com/jay-gauthier-the-mad-jotter-lhon2h/30min?overlayCalendar=true", "_blank")}
            className="h-14 px-12 text-base"
          >
            Schedule a Demo
          </Button>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="border-t border-border/30 py-12 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <p className="text-sm font-light text-muted-foreground">
                © 2024 AthleteNexus. All rights reserved.
              </p>
              <Badge variant="security" className="text-[10px] uppercase tracking-wider">
                SOC 2 Type II Compliant
              </Badge>
            </div>
            <div className="flex items-center gap-8">
              <button className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors">
                Documentation
              </button>
              <button className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors">
                Support
              </button>
              <button className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors">
                Privacy
              </button>
              <button className="text-sm font-light text-muted-foreground hover:text-foreground transition-colors">
                Terms
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
